import type { TOC } from '@ember/component/template-only';
import DonutChart from './charts/donut-chart.gts';
import PieChart from './charts/pie-chart.gts';
import BarChart from './charts/bar-chart.gts';
import HorizontalBarChart from './charts/horizontal-bar-chart.gts';
import LineChart from './charts/line-chart.gts';
import AreaChart from './charts/area-chart.gts';
import SummaryDisplay from './charts/summary-display.gts';
import type { ChartDataPoint, ChartType, ChartOptions, AxisOptions } from '../types/charts.ts';

// ---------------------------------------------------------------------------
// Chart – unified wrapper that delegates to the correct chart component
// based on the @type argument. Drop-in replacement for the Vega setupChart
// modifier pattern.
// ---------------------------------------------------------------------------

interface ChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Which chart type to render */
      type: ChartType;
      /** Fixed height for cartesian charts (default: 300) */
      height?: number;
      /** Ring thickness for donut charts (default: 60) */
      ringThickness?: number;
      /** Show point markers for line charts (default: true) */
      showPoints?: boolean;
      /** Fill opacity for area charts (default: 0.3) */
      fillOpacity?: number;
      /** Label for summary display */
      label?: string;
      /** Compact mode for summary display in dashboard tiles */
      compact?: boolean;
      /** Whether to sort horizontal bars (default: true) */
      sorted?: boolean;
    };
  Element: HTMLDivElement;
}

function isEqual(a: unknown, b: unknown) {
  return a === b;
}

/**
 * Chart – Unified chart component for all chart types
 *
 * @example Donut chart
 * ```gts
 * import Chart from 'tio-ui/components/chart';
 *
 * <Chart @data={{this.data}} @type="donut" @onClick={{this.handleClick}} />
 * ```
 *
 * @example Bar chart
 * ```gts
 * <Chart @data={{this.data}} @type="bar" @xLabel="Month" @yLabel="Revenue" />
 * ```
 *
 * @example Line chart
 * ```gts
 * <Chart @data={{this.data}} @type="line" @showPoints={{true}} />
 * ```
 */
const Chart: TOC<ChartSignature> = <template>
  {{#if (isEqual @type "donut")}}
    <DonutChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @showLegend={{@showLegend}}
      @legendPosition={{@legendPosition}}
      @ringThickness={{@ringThickness}}
      @animate={{@animate}}
      @formatValue={{@formatValue}}
      ...attributes
    />
  {{else if (isEqual @type "pie")}}
    <PieChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @showLegend={{@showLegend}}
      @legendPosition={{@legendPosition}}
      @animate={{@animate}}
      @formatValue={{@formatValue}}
      ...attributes
    />
  {{else if (isEqual @type "bar")}}
    <BarChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @height={{@height}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      ...attributes
    />
  {{else if (isEqual @type "horizontalBar")}}
    <HorizontalBarChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @sorted={{@sorted}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      ...attributes
    />
  {{else if (isEqual @type "line")}}
    <LineChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @height={{@height}}
      @showPoints={{@showPoints}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      ...attributes
    />
  {{else if (isEqual @type "area")}}
    <AreaChart
      @data={{@data}}
      @colors={{@colors}}
      @onClick={{@onClick}}
      @height={{@height}}
      @fillOpacity={{@fillOpacity}}
      @showXAxis={{@showXAxis}}
      @showYAxis={{@showYAxis}}
      @xLabel={{@xLabel}}
      @yLabel={{@yLabel}}
      @formatValue={{@formatValue}}
      ...attributes
    />
  {{else if (isEqual @type "summary")}}
    <SummaryDisplay @data={{@data}} @label={{@label}} @compact={{@compact}} ...attributes />
  {{/if}}
</template>;

export default Chart;

export type { ChartSignature };
